/** @jsxImportSource @emotion/react */
import DobVerify from 'components/landing/hstPaperwork/Verify'
import React, { useEffect, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import Button from 'components/atoms/Button'
import Toast from 'components/atoms/Toast'
import { useLazyGetStatementDownloadQuery } from 'store/services/patientStatements'
import {
  useGetPublicStatementQuery,
  useCreateStatementCheckoutMutation,
} from 'store/services/patientStatementsPublic'
import { formatCalendarDate } from 'helpers/dates'

const StatementPaymentPortal: React.FC = () => {
  const [params] = useSearchParams()
  const location = useLocation()
  const statementId = params.get('statementId') || ''
  const patientId = Number(params.get('patientId') || 0)
  // The website payment page already matched the patient by DOB and stored the
  // statement JWT, so arriving from there skips the DOB step.
  const [verified, setVerified] = useState(
    Boolean((location.state as { verified?: boolean } | null)?.verified),
  )

  const { data: statement, isFetching } = useGetPublicStatementQuery(
    statementId && patientId ? { statementId, patientId } : { statementId: '', patientId: 0 },
    { skip: !statementId || !patientId || !verified },
  )
  const [createCheckout, { isLoading: isCreatingCheckout }] = useCreateStatementCheckoutMutation()
  const [getDownloadUrl] = useLazyGetStatementDownloadQuery()
  // Blank means the whole balance. A patient who can only manage part of it
  // pays what they can and stays in the reminder cycle for the rest.
  const [partialAmount, setPartialAmount] = useState('')
  const [payingPartial, setPayingPartial] = useState(false)

  const balance = Number(statement?.balance || 0)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handlePay = async () => {
    if (!statement) return

    const amount = payingPartial ? Number(partialAmount) : undefined

    if (payingPartial && (!Number.isFinite(amount) || (amount as number) <= 0)) {
      Toast({ type: 'error', label: 'Enter an amount to pay' })
      return
    }

    if (payingPartial && (amount as number) > balance) {
      Toast({ type: 'error', label: `Amount cannot be more than the $${balance.toFixed(2)} due` })
      return
    }

    try {
      const successUrl = `${window.location.origin}/landing/statement/success`
      const cancelUrl = window.location.href
      const response = await createCheckout({
        statementId,
        patientId,
        successUrl,
        cancelUrl,
        amount,
      }).unwrap()
      if (response.url) {
        window.location.href = response.url
      }
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Unable to start checkout'
      Toast({ type: 'error', label: message })
    }
  }

  const handleDownload = async () => {
    try {
      if (!statement?.fileKey) {
        Toast({ type: 'error', label: 'Statement PDF is not available yet' })
        return
      }

      const link = await getDownloadUrl({
        statementId: statement.id,
        patientId: patientId,
        download: true,
      })

      if (link.data?.url) {
        // Not window.open: the URL only arrives after an await, so the click
        // that opened it is no longer the active gesture and mobile web views
        // block the popup. A same-tab anchor is never blocked, and the
        // attachment disposition means the browser saves the file instead of
        // navigating away from the portal.
        const anchor = document.createElement('a')

        anchor.href = link.data.url
        anchor.rel = 'noopener'
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
      }
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Unable to download'
      Toast({ type: 'error', label: message })
    }
  }

  if (!statementId || !patientId) {
    return <div className="container py-5">Invalid link</div>
  }

  if (isFetching) {
    return <div className="container py-5">Loading...</div>
  }

  if (!verified) {
    return (
      <div className="container py-5" css={{ maxWidth: 720 }}>
        <DobVerify
          statementId={statementId}
          nextStep={() => setVerified(true)}
          title={'TestSmarter Payment Portal'}
        />
      </div>
    )
  }

  return (
    <div className="container py-5" css={{ maxWidth: 480 }}>
      <div
        css={{
          textAlign: 'center',
          marginBottom: 40,
        }}
      >
        <h1
          css={{
            fontSize: 36,
            fontWeight: 700,
            color: '#2c3e50',
            marginBottom: 8,
          }}
        >
          Payment Portal
        </h1>
        <div
          css={{
            fontSize: 18,
            color: '#5f6368',
          }}
        >
          Test Smarter
        </div>
      </div>

      <div
        css={{
          backgroundColor: '#fff',
          borderRadius: 8,
          padding: 32,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          marginBottom: 24,
        }}
      >
        <div css={{ marginBottom: 24 }}>
          <div css={{ fontSize: 14, color: '#5f6368', marginBottom: 4 }}>Patient name:</div>
          <div css={{ fontSize: 16, fontWeight: 600, color: '#2c3e50' }}>
            {statement?.patientName || 'John Doe'}
          </div>
        </div>

        <div css={{ marginBottom: 24 }}>
          <div css={{ fontSize: 14, color: '#5f6368', marginBottom: 4 }}>Date of service:</div>
          <div css={{ fontSize: 16, fontWeight: 600, color: '#2c3e50' }}>
            {formatCalendarDate(statement?.appointmentDate, '--')}
          </div>
        </div>

        <div css={{ marginBottom: 24 }}>
          <div css={{ fontSize: 14, color: '#5f6368', marginBottom: 4 }}>Service:</div>
          <div css={{ fontSize: 16, fontWeight: 600, color: '#2c3e50' }}>
            {statement?.serviceDescriptions?.length
              ? statement.serviceDescriptions.join(', ')
              : '--'}
          </div>
        </div>

        <div css={{ borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 12,
              fontSize: 14,
            }}
          >
            <div css={{ color: '#5f6368' }}>Insurance paid</div>
            <div css={{ fontWeight: 600, color: '#2c3e50' }}>
              ${(Number(statement?.payerPaid || 0) + 0).toFixed(2)}
            </div>
          </div>

          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 12,
              fontSize: 14,
            }}
          >
            <div css={{ color: '#5f6368' }}>Write-off</div>
            <div css={{ fontWeight: 600, color: '#2c3e50' }}>
              ${Number(statement?.writeOff || 0).toFixed(2)}
            </div>
          </div>

          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 16,
            }}
          >
            <div css={{ fontWeight: 600, color: '#2c3e50' }}>You owe</div>
            <div css={{ fontWeight: 700, color: '#2c3e50' }}>${balance.toFixed(2)}</div>
          </div>
        </div>
      </div>

      {/* Nothing left to pay, however it got there — a payment just now or a
          balance settled elsewhere. Said as settled rather than as voided. */}
      {statement?.paid || balance <= 0 ? (
        <div
          css={{
            width: '100%',
            padding: '14px 0',
            fontSize: 16,
            fontWeight: 600,
            marginBottom: 12,
            textAlign: 'center',
            backgroundColor: '#d4edda',
            color: '#155724',
            borderRadius: 4,
            border: '1px solid #c3e6cb',
          }}
        >
          ✓ No balance is due — no payment is required
        </div>
      ) : (
        <>
          {payingPartial && (
            <div css={{ marginBottom: 12 }}>
              <label
                htmlFor="statement-partial-amount"
                css={{ fontSize: 14, color: '#5f6368', display: 'block', marginBottom: 4 }}
              >
                Amount to pay
              </label>
              <input
                id="statement-partial-amount"
                className="form-control"
                type="number"
                min="0.01"
                max={balance}
                step="0.01"
                value={partialAmount}
                placeholder={balance.toFixed(2)}
                onChange={(event) => setPartialAmount(event.target.value)}
              />
            </div>
          )}

          <Button
            className="btn btn-primary"
            label={isCreatingCheckout ? 'Processing...' : payingPartial ? 'PAY THIS AMOUNT' : 'PAY NOW'}
            disabled={isCreatingCheckout}
            onClick={handlePay}
            css={{
              width: '100%',
              padding: '14px 0',
              fontSize: 16,
              fontWeight: 700,
              marginBottom: 12,
              textTransform: 'uppercase',
            }}
          />

          <button
            type="button"
            className="btn btn-link"
            onClick={() => {
              setPayingPartial((previous) => !previous)
              setPartialAmount('')
            }}
            css={{ width: '100%', marginBottom: 12, fontSize: 14 }}
          >
            {payingPartial ? 'Pay the full balance instead' : 'Pay a different amount'}
          </button>
        </>
      )}

      <Button
        className="btn btn-outline-primary"
        label="Download Statement PDF"
        onClick={handleDownload}
        css={{
          width: '100%',
          padding: '12px 0',
          fontSize: 16,
        }}
      />
    </div>
  )
}

export default StatementPaymentPortal
